import React, { useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import ClinicalButton from '../../doctor/ui/ClinicalButton';
import ConsultationVaccinationsPanel from '../../doctor/ConsultationVaccinationsPanel';
import { NURSE_COPY } from '../nurseCopy';
import NurseCareChecklist from './NurseCareChecklist';
import NurseTestChecklist from './NurseTestChecklist';
import Icon from '../../common/Icons';
import { careActionDisplayTitle, countCareActionsByKind } from '../utils/nurseCareActRecording';

const TABS = [
  { id: 'care', labelKey: 'CARE_TAB', icon: 'heart' },
  { id: 'injection', labelKey: 'INJECTION_TAB', icon: 'syringe' },
  { id: 'family_planning', labelKey: 'FAMILY_PLANNING_TAB', icon: 'users' },
  { id: 'tests', labelKey: 'TESTS_TAB', icon: 'chartBar' },
  { id: 'vaccines', labelKey: 'VACCINES_TAB', icon: 'shield' },
  { id: 'history', labelKey: 'HISTORY_TAB', icon: 'history' }
];

const P = NURSE_COPY.ACTS_PANEL;

function TimelineFeed({ timeline, loading, onRefresh }) {
  if (loading) {
    return (
      <div className="nr-acts-empty">
        <Spinner animation="border" size="sm" variant="success" />
      </div>
    );
  }

  const items = [];

  (timeline?.vitals || []).forEach((v) => {
    items.push({
      key: `v-${v.id}`,
      at: v.created_at,
      title: 'Constantes vitales',
      body: `TA ${v.bp_systolic || '—'}/${v.bp_diastolic || '—'} · ${v.temperature_c || '—'} °C`
    });
  });

  (timeline?.testResults || []).forEach((t) => {
    items.push({
      key: `tr-${t.id}`,
      at: t.created_at,
      title: t.catalog_name || t.test_name || 'Test',
      body: `${t.result_value}${t.result_unit ? ` ${t.result_unit}` : ''}`
    });
  });

  (timeline?.careActions || []).forEach((a) => {
    items.push({
      key: `a-${a.id}`,
      at: a.created_at,
      title: careActionDisplayTitle(a),
      body: a.description || a.treatment_given || '—'
    });
  });

  (timeline?.vaccinations || []).forEach((v) => {
    const meta = [v.dose_label, v.batch_number ? `Lot ${v.batch_number}` : null]
      .filter(Boolean)
      .join(' · ');
    items.push({
      key: `vx-${v.id}`,
      at: v.administered_at || v.created_at,
      title: v.vaccine_name || 'Vaccination',
      body: meta || 'Vaccination administrée'
    });
  });
  (timeline?.transfers || []).forEach((t) => {
    items.push({
      key: `t-${t.id}`,
      at: t.created_at,
      title: 'Transfert',
      body: t.reason || '—'
    });
  });

  items.sort((a, b) => new Date(b.at) - new Date(a.at));

  if (!items.length) {
    return <p className="nr-acts-empty__text">{P.HISTORY_EMPTY}</p>;
  }

  return (
    <div className="nr-acts-history">
      <div className="nr-acts-history__toolbar">
        <ClinicalButton variant="ghost" compact icon="refresh" onClick={onRefresh} disabled={loading}>
          Actualiser
        </ClinicalButton>
      </div>
      <ul className="nr-timeline-feed">
        {items.map((item) => (
          <li key={item.key} className="nr-timeline-feed__item">
            <time className="nr-timeline-feed__time">
              {new Date(item.at).toLocaleString('fr-FR', {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </time>
            <div className="nr-timeline-feed__body">
              <strong>{item.title}</strong>
              <span>{item.body}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function tabCount(tabId, timeline, actCounts) {
  if (tabId === 'care') return actCounts.care;
  if (tabId === 'injection') return actCounts.injection;
  if (tabId === 'family_planning') return actCounts.family_planning;
  if (tabId === 'tests') return timeline?.testResults?.length || 0;
  if (tabId === 'vaccines') return timeline?.vaccinations?.length || 0;
  if (tabId === 'history') {
    return (
      (timeline?.careActions?.length || 0) +
      (timeline?.testResults?.length || 0) +
      (timeline?.vaccinations?.length || 0) +
      (timeline?.vitals?.length || 0)
    );
  }
  return 0;
}

export default function NurseCareTestPanel({
  consultationId,
  toast,
  timeline,
  timelineLoading,
  onTimelineRefresh
}) {
  const [activeTab, setActiveTab] = useState('care');
  const actCounts = useMemo(() => countCareActionsByKind(timeline?.careActions), [timeline]);

  return (
    <section className="nr-acts-panel nr-workspace__pane">
      <header className="nr-acts-panel__head">
        <div>
          <h2 className="nr-acts-panel__title">{P.TITLE}</h2>
          <p className="nr-acts-panel__desc">{P.DESC}</p>
        </div>
        <nav className="nr-acts-tabs" role="tablist" aria-label={P.TABS_ARIA}>
          {TABS.map((tab) => {
            const count = tabCount(tab.id, timeline, actCounts);
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`nr-acts-tabs__btn${activeTab === tab.id ? ' is-active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon name={tab.icon} size={15} aria-hidden />
                {P[tab.labelKey] || tab.id}
                {count > 0 ? (
                  <span
                    className={`nr-acts-tabs__badge${tab.id === 'history' ? '' : ' nr-acts-tabs__badge--muted'}`}
                  >
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </nav>
      </header>

      <div className="nr-acts-panel__body">
        {activeTab === 'care' || activeTab === 'injection' || activeTab === 'family_planning' ? (
          <NurseCareChecklist
            consultationId={consultationId}
            toast={toast}
            timeline={timeline}
            onTimelineRefresh={onTimelineRefresh}
            kind={activeTab}
          />
        ) : null}

        {activeTab === 'tests' && (
          <NurseTestChecklist
            consultationId={consultationId}
            toast={toast}
            timeline={timeline}
            onTimelineRefresh={onTimelineRefresh}
          />
        )}

        {activeTab === 'vaccines' && (
          <div className="nr-acts-vaccinations">
            <ConsultationVaccinationsPanel
              consultation={{ id: consultationId }}
              embedded
              onSuccess={onTimelineRefresh}
            />
          </div>
        )}

        {activeTab === 'history' && (
          <TimelineFeed timeline={timeline} loading={timelineLoading} onRefresh={onTimelineRefresh} />
        )}
      </div>
    </section>
  );
}
